import { Box } from "@repo/components/src/ui/box";
import { HStack } from "@repo/components/src/ui/hstack";
import { Icon } from "@repo/components/src/ui/icon";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import { formatSimpleMoney } from "@repo/components/src/utils/money";
import { CurrencyCode } from "@repo/components/src/utils/money/currencies";
import { ProductsSelectionQuery } from "@repo/graphql/documents/product/query.generated";
import { BudgetType } from "@repo/graphql/gql/graphql";
import { CreditCard, Gift, Package } from "lucide-react-native";
import React from "react";
import PriceProductRow from "./PriceProductRow";
import type { DraftPreviewModel } from "../draftPreviewTypes";

type ProductNode =
  ProductsSelectionQuery["product"]["list"]["edges"][number]["node"];

type Props = {
  budgetType: string | null;
  fixedBudget: DraftPreviewModel["fixedBudget"];
  priceProduct: string | null;
  priceProductIds: DraftPreviewModel["priceProductIds"];
  giftCardPrice: DraftPreviewModel["giftCardPrice"];
  products: Map<string, ProductNode>;
  /** Fallback currency for a `Fixed` budget the agent has not amount-filled yet. */
  currencyCode: CurrencyCode;
};

/** Ported from `apps/mobil/.../terms/PaymentsCard.tsx`, fed by the draft rather
 * than a published campaign's `campaignpriceproductSet`. */
const PaymentsCard = React.memo(
  ({
    budgetType,
    fixedBudget,
    priceProduct,
    priceProductIds,
    giftCardPrice,
    products,
    currencyCode,
  }: Props) => {
    const cashCard =
      budgetType === BudgetType.Fixed
        ? {
            label: "Pago instantáneo",
            value: formatSimpleMoney(
              fixedBudget ?? { amount: "0", currency: currencyCode },
            ),
            description:
              "Se deposita en tu wallet cuando finaliza la orden de trabajo.",
          }
        : budgetType === BudgetType.Quote
          ? {
              label: "Cotización",
              value: null,
              description:
                "Envíale a la marca un presupuesto por tu contenido. De ser aceptado, se cargará en tu wallet al finalizar la colaboración.",
            }
          : null;

    const giftCardAmount =
      priceProduct === "gift card" && Number(giftCardPrice?.amount ?? 0) > 0
        ? formatSimpleMoney(giftCardPrice ?? undefined)
        : null;

    const priceProducts =
      priceProduct === "yes"
        ? priceProductIds.flatMap(({ productId, quantity }) => {
            const product = products.get(productId);
            return product ? [{ product, quantity }] : [];
          })
        : [];

    const hasAnyPayment =
      cashCard !== null || giftCardAmount !== null || priceProducts.length > 0;
    if (!hasAnyPayment) {
      return (
        <Box className="rounded-3xl bg-white border border-outline-100 px-4 py-4">
          <Text size="sm" className="text-typography-700">
            Sin pagos ni productos en canje configurados.
          </Text>
        </Box>
      );
    }

    return (
      <VStack space="sm">
        {cashCard !== null ? (
          <Box className="rounded-3xl bg-white overflow-hidden border border-outline-100">
            <HStack space="md" className="px-4 py-4 items-start">
              <Box className="rounded-xl items-center justify-center w-9 h-9 bg-brand-50">
                <Icon as={CreditCard} size="sm" className="text-brand-400" />
              </Box>
              <VStack className="flex-1" space="xs">
                <Text
                  bold
                  size="2xs"
                  className="uppercase tracking-widest text-typography-500"
                >
                  {cashCard.label}
                </Text>
                {cashCard.value ? (
                  <Text
                    bold
                    className="text-typography-950 text-[22px] leading-[26px] tracking-tight"
                    numberOfLines={1}
                  >
                    {cashCard.value}
                  </Text>
                ) : null}
                <Text size="xs" className="text-typography-600 leading-[17px]">
                  {cashCard.description}
                </Text>
              </VStack>
            </HStack>
          </Box>
        ) : null}

        {giftCardAmount !== null ? (
          <Box className="rounded-3xl bg-white overflow-hidden border border-outline-100">
            <HStack space="md" className="px-4 py-4 items-start">
              <Box className="rounded-xl items-center justify-center w-9 h-9 bg-brand-50">
                <Icon as={Gift} size="sm" className="text-brand-400" />
              </Box>
              <VStack className="flex-1" space="xs">
                <Text
                  bold
                  size="2xs"
                  className="uppercase tracking-widest text-typography-500"
                >
                  Gift card
                </Text>
                <Text
                  bold
                  className="text-typography-950 text-lg tracking-tight"
                >
                  {giftCardAmount}
                </Text>
              </VStack>
            </HStack>
          </Box>
        ) : null}

        {priceProducts.length > 0 ? (
          <Box className="rounded-3xl bg-white overflow-hidden border border-outline-100">
            <Box className="px-4 pt-4 pb-2">
              <HStack space="xs" className="items-center">
                <Icon as={Package} size="2xs" className="text-brand-400" />
                <Text
                  bold
                  size="2xs"
                  className="uppercase tracking-widest text-brand-400"
                >
                  Productos en canje
                </Text>
              </HStack>
            </Box>
            <VStack>
              {priceProducts.map(({ product, quantity }) => (
                <PriceProductRow
                  key={product.id}
                  product={product}
                  quantity={quantity}
                />
              ))}
            </VStack>
          </Box>
        ) : null}
      </VStack>
    );
  },
);

PaymentsCard.displayName = "PaymentsCard";

export default PaymentsCard;
